import React from "react";
import {
  Text,
  VStack,
  HStack,
  Button,
  Icon,
  Badge,
  Link,
} from "@chakra-ui/react";
import { QrCode as QrCodeIcon, ExternalLink, User, Link2, Edit } from "lucide-react";
import Drawer, {
  DrawerHeader,
  DrawerTitle,
  DrawerBody,
  DrawerFooter,
  DrawerCloseTrigger,
} from "@/components/ui/Drawer";
import DetailsSection from "@/components/ui/DetailsSection";
import withHashDrawer from "@/components/ui/withHashDrawer";
import { BaseDetailsModalProps } from "@/types/DetailsModalTypes";
import { useGetQrCodeQuery } from "../api";
import RelativeTime from "@/components/ui/RelativeTime";

interface QrCodeDetailsModalProps extends BaseDetailsModalProps {
  // No additional props needed for QrCodeDetailsModal
}

const QrCodeDetailsModal: React.FC<QrCodeDetailsModalProps> = ({
  isOpen,
  onClose,
  entityId,
  navigateToModal,
}) => {
  // Fetch individual entity if entityId is provided (hash modal mode)
  const { data: selectedQrCode } = useGetQrCodeQuery(entityId!, {
    skip: !entityId,
  });
  const renderContent = () => {
    if (!selectedQrCode) {
      return <Text>QR Code not found</Text>;
    }

        return (
      <VStack gap={6} align="stretch">
        {/* Basic Information */}
        <DetailsSection
          title="Basic Information"
          icon={User}
          items={[
            {
              label: "ID",
              value: selectedQrCode.id
            },
            {
              label: "Full Name",
              value: selectedQrCode.full_name
            },
            {
              label: "Key",
              value: selectedQrCode.key,
              render: (value) => <Text fontFamily="mono">{value}</Text>
            },
            {
              label: "Created",
              value: selectedQrCode.created_at,
              render: () => <RelativeTime date={selectedQrCode.created_at} />
            }
          ]}
        />

        {/* Links & URLs */}
        <DetailsSection
          title="Links & URLs"
          icon={Link2}
          items={[
            {
              label: "Destination URL",
              value: selectedQrCode.destination_link,
              render: () => (
                <VStack align="end" gap={2}>
                  <Link
                    href={selectedQrCode.destination_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    fontFamily="mono"
                    fontSize="sm"
                    color="blue.600"
                    _dark={{ color: "blue.400" }}
                    wordBreak="break-all"
                    textAlign="right"
                  >
                    {selectedQrCode.destination_link}
                  </Link>
                </VStack>
              )
            },
            {
              label: "QR Code Link",
              value: selectedQrCode.qrcode_link,
              render: () => (
                <Text
                  fontFamily="mono"
                  fontSize="sm"
                  color="gray.600"
                  _dark={{ color: "gray.400" }}
                  wordBreak="break-all"
                  textAlign="right"
                >
                  {selectedQrCode.qrcode_link}
                </Text>
              )
            },
            {
              label: "Opened",
              value: selectedQrCode.opened ? 'yes' : 'no',
              render: () => (
                <Badge
                  size="sm"
                  colorPalette={selectedQrCode.opened ? 'green' : 'gray'}
                  variant="subtle"
                >
                  {selectedQrCode.opened ? 'Yes' : 'No'}
                </Badge>
              )
            }
          ]}
        />

        {/* Status & Activity */}
        <DetailsSection
          title="Status & Activity"
          icon={QrCodeIcon}
          items={[
            {
              label: "Updated",
              value: selectedQrCode.updated_at,
              render: () => (
                <RelativeTime 
                  date={selectedQrCode.updated_at || selectedQrCode.created_at} 
                  fallbackDate={selectedQrCode.created_at} 
                />
              )
            }
          ]}
        />
      </VStack>
    );
    };

    return (
      <Drawer isOpen={isOpen} onClose={onClose} size="lg">
        <DrawerHeader>
          <DrawerTitle color="brand.700" _dark={{ color: "brand.300" }}>
            QR Code Pixel Share Details
          </DrawerTitle>
          <DrawerCloseTrigger />
        </DrawerHeader>

        <DrawerBody>
          {renderContent()}
        </DrawerBody>
        
        <DrawerFooter>
          <HStack justify="space-between" w="full">
            <HStack>
              {entityId && navigateToModal && (
                <Button 
                  variant="outline" 
                  onClick={() => navigateToModal(entityId, 'edit')}
                >
                  <Icon as={Edit} boxSize={4} mr={2} />
                  Edit
                </Button>
              )}
              {selectedQrCode?.destination_link.startsWith('http') && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => window.open(selectedQrCode.destination_link, '_blank')}
                >
                  <Icon as={ExternalLink} boxSize={3.5} mr={2} />
                  Open Link
                </Button>
              )}
            </HStack>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </HStack>
        </DrawerFooter>
      </Drawer>
    );
  };

// Export wrapped version for hash modal (details modal: view action)
export default withHashDrawer(QrCodeDetailsModal, {
  action: 'view' // Default view action
}); 